import { useState, useEffect, useRef } from 'react';
import { useFirstMountState } from 'react-use';
import { MessageList, useChannelStateContext } from 'stream-chat-react';
import { makeStyles } from '@material-ui/styles';
import { Theme, CloseFill } from '@bizzabo/design-system';
import CustomMessage from '../CustomMessage';
import { UserRole } from '../../types/UserRole';
import { isDeletedMessage } from '../CustomMessage/Utils/MessageUtils';
import { IChatChannel } from '../../interfaces/IChatChannel';
import { onMessageUpdatedOrDeleted } from '../SessionChat/messageUpdateSubscribe';
import PinnedBar from './PinnedBar';
import useTypeTracking from '../../analytics';

type Props = {
    chatChannel: IChatChannel;
    userRole: UserRole;
    onShowingPinnedChange: React.Dispatch<React.SetStateAction<boolean>>;
    showPinView?: boolean;
};

const PinnedMessages: React.FC<Props> = ({
    userRole,
    chatChannel,
    onShowingPinnedChange,
    showPinView,
}) => {
    const { channel } = useChannelStateContext();
    const isFirstMount = useFirstMountState();
    const pinnedMessages = channel.state.pinnedMessages
        .filter(message => !isDeletedMessage(message))
        .sort((a, b) => {
            return new Date(a.pinned_at).getTime() - new Date(b.pinned_at).getTime();
        });
    const pinnedMessagesAmount = pinnedMessages.length;
    const [isShowingPinned, _setIsShowingPinned] = useState(false);
    const [shouldShowPinnedBar, setShouldShowPinnedBar] = useState(pinnedMessagesAmount > 0);
    const isShowingPinnedRef = useRef(isShowingPinned);
    const setIsShowingPinned = isToDisplay => {
        isShowingPinnedRef.current = isToDisplay;
        _setIsShowingPinned(isToDisplay);
        onShowingPinnedChange(isToDisplay);
    };
    const { Track, trackClickEvent } = useTypeTracking({
        data: {
            description: {
                is_pinned_page_open: isShowingPinned,
            },
        },
    });
    useEffect(() => {
        const showHidePinnedAndBar = () => {
            if (channel.state.pinnedMessages.length < 1) {
                setIsShowingPinned(false);
                setShouldShowPinnedBar(false);
            } else {
                if (!isShowingPinnedRef.current) {
                    setShouldShowPinnedBar(true);
                }
            }
        };
        return onMessageUpdatedOrDeleted(channel, showHidePinnedAndBar);
    }, [channel]);

    useEffect(() => {
        if (isFirstMount) {
            return;
        }
        isShowingPinned ? setShouldShowPinnedBar(false) : setShouldShowPinnedBar(true);
    }, [isShowingPinned]);

    useEffect(() => {
        if (showPinView === null) {
            return;
        }
        setIsShowingPinned(showPinView);
    }, [showPinView]);

    const handleClosePinnedBar = () => {
        setIsShowingPinned(false);
        trackClickEvent({
            buttonType: 'PIN',
            subCategory: 'CLOSE_ALL_PINNED_MESSAGES',
        });
    };

    const lastPinnedMessage = pinnedMessages[pinnedMessagesAmount - 1];

    const classes = useStyles();

    return (
        <Track>
            {isShowingPinned && (
                <div className={classes.listContainer}>
                    <div onClick={handleClosePinnedBar}>
                        <CloseFill className={classes.closeIcon} />
                    </div>
                    <MessageList
                        messages={pinnedMessages}
                        Message={() => (
                            <CustomMessage chatChannel={chatChannel} userRole={userRole} />
                        )}
                        disableDateSeparator
                    />
                </div>
            )}
            {shouldShowPinnedBar && (
                <PinnedBar
                    onClick={() => setIsShowingPinned(true)}
                    lastPinnedMessage={lastPinnedMessage}
                    pinnedMessagesAmount={pinnedMessagesAmount}
                />
            )}
        </Track>
    );
};

const useStyles = makeStyles(
    (theme: Theme) => ({
        listContainer: {
            height: '100%',
            position: 'relative',
            '& .str-chat__list': {
                height: '100%',
                backgroundColor: theme.palette.accent[100],
                borderRadius: '0 0 33px 33px',
            },
        },
        closeIcon: {
            cursor: 'pointer',
            position: 'absolute',
            zIndex: 1,
            top: 8,
            right: 15,
            width: 32,
            height: 32,
            padding: 4,
            backgroundColor: theme.palette.greys[100],
            borderRadius: '50%',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
        },
    }),
    {
        classNamePrefix: 'PinnedMessagesBar',
    }
);

export default PinnedMessages;
